import React, { useState, useEffect } from 'react';
import { 
  FaSearch, 
  FaFilter,
  FaSort,
  FaEye,
  FaFileExport,
  FaFilePdf,
  FaMoneyBillWave,
  FaCheckCircle,
  FaClock,
  FaChartLine,
  FaTimesCircle,
  FaUndo,
  FaTimes
} from 'react-icons/fa';
import './PaymentData.css';

const PaymentData = ({ data, onClose, currentYearRange, currentMonthRange, currentMonthName}) => {
  const [filteredData, setFilteredData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortConfig, setSortConfig] = useState({ key: 'createdAt', direction: 'desc' });
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  
  // Handle sort
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Get current items for pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);

  // Change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // View payment details
  const viewPaymentDetails = (payment) => {
    setSelectedPayment(payment);
  };

  // Close payment details
  const closePaymentDetails = () => {
    setSelectedPayment(null);
  };

  // Convert to EST timezone
  const convertToEST = (dateString) => {
    if (!dateString) return null;
    
    try {
      const date = new Date(dateString);
      // Convert to EST (UTC-5) considering daylight saving time
      const estOffset = date.getTimezoneOffset() >= 300 ? 300 : 240; // EST is UTC-5, EDT is UTC-4
      const estTime = new Date(date.getTime() - (estOffset * 60 * 1000));
      return estTime;
    } catch (e) {
      return null;
    }
  };

  // Format date in EST
  const formatDateEST = (dateString) => {
    const estDate = convertToEST(dateString);
    if (!estDate) return 'N/A';
    
    return estDate.toLocaleString('en-US', {
      timeZone: 'America/New_York',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Export data to CSV
  const exportToCSV = () => {
    const headers = [
      'Date (EST)', 'Client', 'Email', 'Order ID', 'Document Type', 'Amount', 
      'Discounted Amount', 'Transaction ID', 'Status', 'User Role', 'Origin'
    ].join(',');
    
    const csvData = filteredData.map(payment => {
      const estDate = convertToEST(payment.createdAt);
      const formattedDate = estDate ? estDate.toLocaleString('en-US', {
        timeZone: 'America/New_York',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      }) : 'N/A';
      
      return [
        formattedDate,
        `"${payment.paypalClient}"`,
        payment.userEmail,
        payment.orderId || 'New Project',
        payment.formData ? payment.formData.replace(/"/g, '""') : 'N/A',
        payment.amount,
        payment.discountedTotalCost,
        payment.paypalTransactionId,
        payment.status,
        payment.userRole,
        payment.origin
      ].join(',');
    }).join('\n');
    
    const csv = `${headers}\n${csvData}`;
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'payment-data.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  // Export data to PDF
  const exportToPDF = () => {
    // Create a printable version of the table
    const printContent = document.getElementById('payment-table-container').innerHTML;
    const originalContent = document.body.innerHTML;
    
    document.body.innerHTML = `
      <div style="padding: 20px;">
        <h1>Payment Data Report</h1>
        <p>Generated on: ${new Date().toLocaleString('en-US', { timeZone: 'America/New_York' })} (EST)</p>
        ${printContent}
      </div>
    `;
    
    window.print();
    document.body.innerHTML = originalContent;
    window.location.reload();
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  // Get status icon
  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <FaCheckCircle className="status-icon completed" />;
      case 'pending':
        return <FaClock className="status-icon pending" />;
      case 'failed':
        return <FaTimesCircle className="status-icon failed" />;
      case 'refunded':
        return <FaUndo className="status-icon refunded" />;
      default:
        return <FaClock className="status-icon" />;
    }
  };

  // Filter data for current year and current month
  const currentYearData = filteredData.filter(payment => {
    if (!payment.createdAt) return false;
    const paymentDate = new Date(payment.createdAt);
    return paymentDate >= currentYearRange.start && paymentDate <= currentYearRange.end;
  });

  const currentMonthData = filteredData.filter(payment => {
    if (!payment.createdAt) return false;
    const paymentDate = new Date(payment.createdAt);
    return paymentDate >= currentMonthRange.start && paymentDate <= currentMonthRange.end;
  });

  const totalRevenue = currentYearData.reduce((sum, payment) => sum + (payment.amount || 0), 0);
  const monthRevenue = currentMonthData.reduce((sum, payment) => sum + (payment.amount || 0), 0);
  const completedCount = currentYearData.filter(p => p.status === 'completed').length;
  const pendingCount = currentYearData.filter(p => p.status === 'pending').length;

  // Helper functions to get current year and month ranges
  const getCurrentYearRange = () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), 0, 1); // January 1st of current year
    const end = new Date(now.getFullYear(), 11, 31, 23, 59, 59); // December 31st of current year
    return { start, end };
  };  

  const getCurrentMonthRange = () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1); // First day of current month
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59); // Last day of current month
    return { start, end };
  };                  

  const getMonthName = (monthIndex) => {
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    return months[monthIndex];
  };  

  // Use the data passed as prop
  useEffect(() => {
    if (data && data.data) {
      setFilteredData(data.data || []);
    } else {
      setFilteredData(data || []);
    }
  }, [data]);

  // Apply filters and sorting
  useEffect(() => {
    let result = [];
    
    // Handle both data structures: {data: [...]} or direct array
    const paymentArray = data && data.data ? data.data : data;
    
    if (paymentArray && Array.isArray(paymentArray)) {
      result = [...paymentArray];
      
      // Apply search filter
      if (searchTerm) {
        const lowerSearchTerm = searchTerm.toLowerCase();
        result = result.filter(payment => 
          payment.paypalClient?.toLowerCase().includes(lowerSearchTerm) ||
          payment.userEmail?.toLowerCase().includes(lowerSearchTerm) ||
          (payment.orderId || 'New Project')?.toLowerCase().includes(lowerSearchTerm) ||
          payment.paypalTransactionId?.toLowerCase().includes(lowerSearchTerm) ||
          payment.formData?.toLowerCase().includes(lowerSearchTerm)
        );
      }
      
      // Apply status filter
      if (statusFilter !== 'all') {
        result = result.filter(payment => payment.status === statusFilter);
      }
      
      // Apply sorting
      if (sortConfig.key) {
        result.sort((a, b) => {
          const aValue = a[sortConfig.key];
          const bValue = b[sortConfig.key];
          
          if (aValue < bValue) {
            return sortConfig.direction === 'asc' ? -1 : 1;
          }
          if (aValue > bValue) {
            return sortConfig.direction === 'asc' ? 1 : -1;
          }
          return 0;
        });
      }
    }
    
    setFilteredData(result);
    setCurrentPage(1);
  }, [searchTerm, statusFilter, sortConfig, data]);

  


  

  return (
    <div className="payment-data">
      {/* Header with Stats */}
      <div className="payment-header">
        <div className="header-title">
          <FaMoneyBillWave className="header-icon" />
          <h2>Payment Dashboard</h2>
        </div>
        <div className="header-stats">
          <div className="stat-item">
            <span className="stat-value">{currentYearData.length}</span>
            <span className="stat-label">Year Payments</span>
          </div>
          <div className="stat-item">
            <span className="stat-value">{formatCurrency(totalRevenue)}</span>
            <span className="stat-label">{new Date().getFullYear()} Revenue</span>
          </div>
          <div className="stat-item">
            <span className="stat-value">{formatCurrency(monthRevenue)}</span>
            <span className="stat-label">{currentMonthName} Revenue</span>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="payment-controls">
        <div className="search-box">
          <FaSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search payments..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        
        <div className="filter-control">
          <FaFilter className="filter-icon" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Statuses</option>
            <option value="completed">Completed</option>
            <option value="pending">Pending</option>
            <option value="failed">Failed</option>
            <option value="refunded">Refunded</option>
          </select>
        </div>

        <div className="export-buttons">
          <button className="export-btn csv" onClick={exportToCSV}>
            <FaFileExport /> CSV
          </button>
          <button className="export-btn pdf" onClick={exportToPDF}>
            <FaFilePdf /> PDF
          </button>
        </div>
      </div>

      {/* Statistics Overview */}
      <div className="stats-overview">
        <div className="stat-card revenue">
          <div className="stat-content">
            <h3>{formatCurrency(totalRevenue)}</h3>
            <p>{new Date().getFullYear()} Revenue</p>
          </div>
          <FaMoneyBillWave className="stat-icon" />
        </div>

        <div className="stat-card completed">
          <div className="stat-content">
            <h3>{completedCount}</h3>
            <p>Completed</p>
          </div>
          <FaCheckCircle className="stat-icon" />
        </div>

        <div className="stat-card pending">
          <div className="stat-content">
            <h3>{pendingCount}</h3>
            <p>Pending</p>
          </div>
          <FaClock className="stat-icon" />
        </div>

        <div className="stat-card average">
          <div className="stat-content">
            <h3>{formatCurrency(monthRevenue)}</h3>
            <p>{currentMonthName} Revenue</p>
          </div>
          <FaChartLine className="stat-icon" />
        </div>
      </div>

      {/* Payments Table */}
      <div className="table-container" id="payment-table-container">
        <table className="payment-table">
          <thead>
            <tr>
              <th onClick={() => handleSort('createdAt')} className="sortable">
                Date (EST) <FaSort />
              </th>
              <th onClick={() => handleSort('paypalClient')} className="sortable">
                Client <FaSort />
              </th>
              <th onClick={() => handleSort('userEmail')} className="sortable">
                Email <FaSort />
              </th>
              <th onClick={() => handleSort('formData')} className="sortable">
                Document Type <FaSort />
              </th>
              <th onClick={() => handleSort('amount')} className="sortable">
                Amount <FaSort />
              </th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentItems.length > 0 ? (
              currentItems.map((payment) => (
                <tr key={payment.id || payment.paypalTransactionId} className="payment-row">
                  <td className="date-cell">{formatDateEST(payment.createdAt)}</td>
                  <td className="client-cell">{payment.paypalClient || 'N/A'}</td>
                  <td className="email-cell">{payment.userEmail}</td>
                  <td className="document-cell">
                    {payment.formData ? payment.formData.replace(/"/g, '') : 'N/A'}
                  </td>
                  <td className="amount-cell">{formatCurrency(payment.amount)}</td>
                  <td className="status-cell">
                    <span className={`status-badge ${payment.status}`}>
                      {getStatusIcon(payment.status)}
                      {payment.status}
                    </span>
                  </td>
                  <td className="actions-cell">
                    <button 
                      className="view-btn"
                      onClick={() => viewPaymentDetails(payment)}
                      title="View payment details"
                    >
                      <FaEye /> Details
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="7" className="no-data">
                  {searchTerm || statusFilter !== 'all' ? 'No payments match your search criteria' : 'No payment records found'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {filteredData.length > itemsPerPage && (
        <div className="pagination">
          <button 
            onClick={() => paginate(currentPage - 1)} 
            disabled={currentPage === 1}
            className="pagination-btn"
          >
            ← Previous
          </button>
          
          <div className="pagination-numbers">
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const pageNum = i + 1;
              return (
                <button
                  key={pageNum}
                  onClick={() => paginate(pageNum)}
                  className={`pagination-btn ${currentPage === pageNum ? 'active' : ''}`}
                >
                  {pageNum}
                </button>
              );
            })}
            
            {totalPages > 5 && (
              <span className="pagination-ellipsis">...</span>
            )}
          </div>

          <button 
            onClick={() => paginate(currentPage + 1)} 
            disabled={currentPage === totalPages}
            className="pagination-btn"
          >
            Next →
          </button>
        </div>
      )}

      {/* Payment Details Modal */}
      {selectedPayment && (
        <div className="payment-details-modal">
          <div className="modal-overlay" onClick={closePaymentDetails}></div>
          <div className="modal-content">
            <div className="modal-header">
              <h3>Payment Details</h3>
              <button className="close-modal" onClick={closePaymentDetails}>
                <FaTimes />
              </button>
            </div>
            <div className="modal-body">
              <div className="detail-grid">
                <div className="detail-item">
                  <label>Transaction ID</label>
                  <p>{selectedPayment.paypalTransactionId}</p>
                </div>
                <div className="detail-item">
                  <label>Date & Time (EST)</label>
                  <p>{formatDateEST(selectedPayment.createdAt)}</p>
                </div>
                <div className="detail-item">
                  <label>Client Name</label>
                  <p>{selectedPayment.paypalClient || 'N/A'}</p>
                </div>
                <div className="detail-item">
                  <label>Email</label>
                  <p>{selectedPayment.userEmail}</p>
                </div>
                <div className="detail-item">
                  <label>Order ID</label>
                  <p>{selectedPayment.orderId || 'New Project'}</p>
                </div>
                <div className="detail-item">
                  <label>Document Type</label>
                  <p>{selectedPayment.formData ? selectedPayment.formData.replace(/"/g, '') : 'N/A'}</p>
                </div>
                <div className="detail-item">
                  <label>Amount</label>
                  <p className="amount">{formatCurrency(selectedPayment.amount)}</p>
                </div>
                <div className="detail-item">
                  <label>Discounted Amount</label>
                  <p>{formatCurrency(selectedPayment.discountedTotalCost)}</p>
                </div>
                <div className="detail-item">
                  <label>Status</label>
                  <p>
                    <span className={`status-badge ${selectedPayment.status}`}>
                      {getStatusIcon(selectedPayment.status)}
                      {selectedPayment.status}
                    </span>
                  </p>
                </div>
                <div className="detail-item">
                  <label>User Role</label>
                  <p>{selectedPayment.userRole}</p>
                </div>
                <div className="detail-item full-width">
                  <label>Origin</label>
                  <p>{selectedPayment.origin}</p>
                </div>
                <div className="detail-item full-width">
                  <label>Subdomain</label>
                  <p>{selectedPayment.subdomain}</p>
                </div>
              </div>
              <div className="modal-actions">
                <button className="download-btn" onClick={() => {
                  const content = document.querySelector('.modal-content').innerHTML;
                  const originalContent = document.body.innerHTML;
                  
                  document.body.innerHTML = content;
                  window.print();
                  document.body.innerHTML = originalContent;
                  window.location.reload();
                }}>
                  <FaFilePdf /> Download as PDF
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentData;